import type { ReactNode } from "react";
import { Check, CheckCircle2, History, Info } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { formatDateTime } from "@/utils/format";
import {
  TIMELINE_EVENT_LABELS,
  type SolicitacaoTimelineEvent,
  type SolicitacaoTimelineEventType,
  type SolicitacaoPagamentoDto,
} from "@/services/solicitacaoPagamento";

interface Props {
  open: boolean;
  onClose: () => void;
  item: SolicitacaoPagamentoDto;
}

type NoteVariant = "info" | "success" | "warning";

function noteVariant(type: SolicitacaoTimelineEventType): NoteVariant | null {
  if (type === "titulo_sienge_inserido") return "success";
  if (type === "reprovada_operacional" || type === "reprovada_financeira" || type === "cancelada") return "warning";
  if (
    type === "criada" ||
    type === "titulo_sienge_preview" ||
    type === "reenviada"
  ) {
    return "info";
  }
  return null;
}

function TimelineNoteBox({
  variant,
  children,
}: {
  variant: NoteVariant;
  children: ReactNode;
}) {
  if (variant === "success") {
    return (
      <div className="flex items-start gap-2.5 mt-2.5 rounded-xl bg-emerald-50 border border-emerald-100 px-3.5 py-2.5">
        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
        <p className="text-sm text-emerald-800 leading-snug">{children}</p>
      </div>
    );
  }
  if (variant === "warning") {
    return (
      <div className="flex items-start gap-2.5 mt-2.5 rounded-xl bg-amber-50 border border-amber-100 px-3.5 py-2.5">
        <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
        <p className="text-sm text-amber-900 leading-snug">{children}</p>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2.5 mt-2.5 rounded-xl bg-sky-50 border border-sky-100 px-3.5 py-2.5">
      <Info className="w-4 h-4 text-sky-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
      <p className="text-sm text-sky-900 leading-snug">{children}</p>
    </div>
  );
}

function TimelineStep({
  event,
  isLast,
}: {
  event: SolicitacaoTimelineEvent;
  isLast: boolean;
}) {
  const label = TIMELINE_EVENT_LABELS[event.type] ?? event.type;
  const boxVariant = event.note ? noteVariant(event.type) : null;

  return (
    <li className="relative flex gap-4">
      <div className="flex flex-col items-center flex-shrink-0 w-5">
        {isLast ? (
          <div
            className="w-5 h-5 rounded-full bg-brand-700 flex-shrink-0 ring-4 ring-white relative z-10"
            aria-hidden
          />
        ) : (
          <div
            className="w-5 h-5 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 ring-4 ring-white relative z-10"
            aria-hidden
          >
            <Check className="w-3 h-3 text-sky-600" strokeWidth={3} />
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1 pb-7">
        <p className="text-sm font-bold text-slate-900 leading-snug">{label}</p>
        <p className="text-xs text-slate-500 mt-1">
          {formatDateTime(event.at)} • {event.userName}
        </p>
        {event.note && boxVariant && <TimelineNoteBox variant={boxVariant}>{event.note}</TimelineNoteBox>}
        {event.note && !boxVariant && (
          <p className="text-sm text-slate-600 mt-2 rounded-xl bg-slate-50 border border-slate-100 px-3.5 py-2.5">
            {event.note}
          </p>
        )}
      </div>
    </li>
  );
}

export function SolicitacaoPagamentoTimelineModal({ open, onClose, item }: Props) {
  const events = [...(item.timeline ?? [])].sort(
    (a, b) => new Date(a.at).getTime() - new Date(b.at).getTime(),
  );

  const modalTitle = (
    <div className="flex items-center gap-3 min-w-0">
      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
        <History className="w-4 h-4 text-brand-700" strokeWidth={2.25} />
      </div>
      <span className="font-bold text-slate-900 truncate">Leadtime — {item.descricao}</span>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={modalTitle}
      size="md"
      footer={
        <div className="flex items-center justify-end w-full">
          <Button type="button" onClick={onClose}>
            Fechar
          </Button>
        </div>
      }
    >
      {events.length === 0 ? (
        <p className="text-sm text-slate-500 py-2">Nenhum evento registrado.</p>
      ) : (
        <ol className="relative pl-0.5">
          {events.length > 1 && (
            <span
              className="absolute left-[10px] top-2.5 bottom-6 w-px bg-slate-200"
              aria-hidden
            />
          )}
          {events.map((ev, idx) => (
            <TimelineStep
              key={ev.id}
              event={ev}
              isLast={idx === events.length - 1}
            />
          ))}
        </ol>
      )}
    </Modal>
  );
}
